import React from 'react';
import {
  Button,
  Collapse,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@material-ui/core';
import { Add, ExpandLess, ExpandMore } from '@material-ui/icons';
import { getWifiSignalIcon, rssiToSignalQuality } from '../common/WifiSignal';
import { NearbyWifiNetwork } from '../../stores/TelematicsDevice/DeviceTelemetryApi';
import { PasswordField } from './PasswordField';
import { NewProfile, formatScannedNetworkSummary, isOpenSecurity } from './wifiSetupModel';

type NearbyNetworksPickerProps = {
  networks: NearbyWifiNetwork[];
  loading: boolean;
  selectedKey: string | null;
  onToggle: (network: NearbyWifiNetwork, networkKey: string) => void;
  newProfile: NewProfile;
  onPasswordChange: (value: string) => void;
  showPassword: boolean;
  onToggleShowPassword: () => void;
  onAdd: () => void;
};

export const NearbyNetworksPicker: React.FC<NearbyNetworksPickerProps> = ({
  networks,
  loading,
  selectedKey,
  onToggle,
  newProfile,
  onPasswordChange,
  showPassword,
  onToggleShowPassword,
  onAdd,
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Typography variant="body2" color="textSecondary">
        Loading nearby networks...
      </Typography>
    );
  }

  if (networks.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        No nearby networks were found in the last scan. Switch to “Enter manually” to add one yourself.
      </Typography>
    );
  }

  return (
    <>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Tap a network to add it.
      </Typography>
      <List dense disablePadding>
        {networks.map((network, index) => {
          const networkKey = `${index}-${network.ssid}`;
          const isSelected = networkKey === selectedKey;

          return (
            <React.Fragment key={networkKey}>
              <ListItem
                button
                selected={isSelected}
                onClick={() => onToggle(network, networkKey)}
                style={{
                  borderLeft: `4px solid ${isSelected ? theme.palette.primary.main : 'transparent'}`,
                }}
              >
                <ListItemIcon>{getWifiSignalIcon(rssiToSignalQuality(network.rssi))}</ListItemIcon>
                <ListItemText
                  primary={network.ssid}
                  secondary={formatScannedNetworkSummary(network)}
                  primaryTypographyProps={{ style: { fontWeight: isSelected ? 600 : 400 } }}
                />
                {isSelected ? <ExpandLess color="action" /> : <ExpandMore color="action" />}
              </ListItem>
              <Collapse in={isSelected} timeout="auto" unmountOnExit>
                <div
                  style={{
                    padding: '12px 16px 16px',
                    borderLeft: `4px solid ${theme.palette.primary.main}`,
                    backgroundColor: theme.palette.action.hover,
                  }}
                >
                  <Grid container spacing={2}>
                    {!isOpenSecurity(newProfile.securityType) && (
                      <Grid item xs={12}>
                        <PasswordField
                          value={newProfile.password}
                          onChange={onPasswordChange}
                          showPassword={showPassword}
                          onToggleShowPassword={onToggleShowPassword}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={onAdd}
                        disabled={!newProfile.ssid.trim()}
                        startIcon={<Add />}
                      >
                        Add network
                      </Button>
                    </Grid>
                  </Grid>
                </div>
              </Collapse>
            </React.Fragment>
          );
        })}
      </List>
    </>
  );
};
